import { useNavigate } from "react-router-dom";
import { Card } from "react-bootstrap"
import PropTypes from "prop-types";
import { AppHelpers } from "../../Helpers/App/AppHelpers";

import "./HomeCard.css";

/**
 * HomeCard component used for displaying a clickable card with an icon and label.
 * 
 * Navigates to a specific route when clicked or when the "Enter" key is pressed (accessibility supported).
 * Can be optionally disabled by passing the `disabled` prop.
 *
 * @component
 * @param {Object} props - Props for the HomeCard component.
 * @param {string} props.label - Text label displayed in the card footer.
 * @param {string} props.image - Image name or key used to fetch the Base64-encoded SVG icon.
 * @param {boolean} props.disabled - Whether the card is disabled (non-interactive).
 * @param {string} props.navigateTo - Route path to navigate to on click.
 *
 * @example
 * <HomeCard
 *   label="Dashboard"
 *   image="dashboard-icon"
 *   disabled={false}
 *   navigateTo="/dashboard"
 * />
 */
const HomeCard = (props) => {
    const { label, image, disabled, navigateTo, className } = props;
    const navigate = useNavigate();

    const handleClick = () => navigate(navigateTo);
    const containerCSS = () => `home-card focusable ${className} ${disabled ? "disabled" : ""}`;

    return (
        <Card className={containerCSS()} role="button" aria-label={label} onKeyUp={AppHelpers.clickOnEnter} tabIndex="0" onClick={handleClick}>
            <Card.Body
            className="home-card__icon-container"
            {...(!disabled ? { tabIndex: 0 } : {})}
            >
                <img className="home-card__icon" src={AppHelpers.svgBase64(image)} alt={label} />
            </Card.Body>
            <Card.Footer className="home-card__label">
                <p className="bold">{label}</p>
            </Card.Footer>
        </Card>
    );
}

HomeCard.propTypes = {
    label: PropTypes.string,
    image: PropTypes.string,
    disabled: PropTypes.bool,
    navigateTo: PropTypes.string,
    className: PropTypes.string,
}

export default HomeCard;
